import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/Panel";
import { ClienteForm, CLIENTE_VACIO, validarCliente } from "@/components/ClienteForm";
import { useBusquedaHeader } from "@/lib/busquedaHeader";
import { CheckCircle2, X } from "lucide-react";

export default function ClienteCrear() {
  const navigate = useNavigate();
  const [valor, setValor] = useState(CLIENTE_VACIO);
  const [errores, setErrores] = useState({});
  const [exito, setExito] = useState(false);
  const [dup, setDup] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [avisoApi, setAvisoApi] = useState("");

  const buscarCedula = async (cedula) => {
    setExito(false);
    setAvisoApi("");
    try {
      const existentes = await api.get("/clientes", { params: { person_id: cedula } });
      if (existentes.data[0]) {
        setValor({ ...CLIENTE_VACIO, person_id: cedula });
        toast.info("Ya existe un cliente con ese ID. Use el botón Autocompletar para abrirlo.");
        return;
      }
      const { data } = await api.get(`/cedula/${cedula}`);
      if (data.encontrado) {
        setValor({ ...CLIENTE_VACIO, person_id: cedula, nombre: data.nombre,
          primer_apellido: data.primer_apellido, segundo_apellido: data.segundo_apellido, tipo: data.tipo });
        setErrores({});
        toast.success("Datos autocompletados desde el registro. Revíselos y complete el resto.");
      } else {
        setValor({ ...CLIENTE_VACIO, person_id: cedula });
        setAvisoApi("No se pudo autocompletar, complete manualmente");
        toast.warning("No se pudo autocompletar, complete manualmente");
      }
    } catch {
      setValor({ ...CLIENTE_VACIO, person_id: cedula });
      setAvisoApi("No se pudo autocompletar, complete manualmente");
      toast.warning("No se pudo autocompletar, complete manualmente");
    }
  };

  useBusquedaHeader("Buscar cliente por número de ID (cédula)...", true, buscarCedula);

  useEffect(() => {
    const pid = valor.person_id.trim();
    if (pid.length < 3) { setDup(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/clientes", { params: { person_id: pid } });
        setDup(r.data[0] || null);
      } catch { setDup(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [valor.person_id]);

  const autocompletar = () => {
    setValor({ ...CLIENTE_VACIO, ...Object.fromEntries(Object.keys(CLIENTE_VACIO).map((k) => [k, dup[k] ?? ""])) });
    setErrores({});
  };

  const guardar = async () => {
    const e = validarCliente(valor);
    if (dup) e.person_id = "Ya existe un cliente con ese número de ID";
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true);
    try {
      const { data } = await api.post("/clientes", valor);
      toast.success("Cliente creado de forma exitosa");
      navigate("/vehiculos", { state: { clienteId: data.id } });
    } catch (err) {
      const msg = errorMsg(err);
      if (err?.response?.status === 409) setErrores({ person_id: msg });
      toast.error(msg);
    } finally { setGuardando(false); }
  };

  const aviso = dup ? (
    <div data-testid="cliente-duplicado-aviso"
      className="mt-1 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
      <span>Ya existe: <b>{dup.nombre} {dup.primer_apellido}</b></span>
      <button type="button" data-testid="cliente-autocompletar-btn" onClick={autocompletar}
        className="font-medium text-indigo-600 underline whitespace-nowrap">Autocompletar</button>
    </div>
  ) : null;

  return (
    <div data-testid="cliente-crear-page" className="space-y-4">
      {exito && (
        <div data-testid="cliente-exito-alert"
          className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
          <CheckCircle2 size={18} className="text-green-600 shrink-0" />
          Cliente creado de forma exitosa
          <button data-testid="cliente-exito-cerrar" onClick={() => setExito(false)} className="ml-auto text-green-700 hover:text-green-900">
            <X size={16} />
          </button>
        </div>
      )}
      {avisoApi && (
        <div data-testid="cliente-aviso-api" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          {avisoApi}
          <button onClick={() => setAvisoApi("")} className="ml-auto text-amber-700 hover:text-amber-900"><X size={14} /></button>
        </div>
      )}
      <Panel titulo="Creación de cliente" testId="cliente-crear-panel">
        <ClienteForm valor={valor} onChange={(v) => { setValor(v); setExito(false); }} errores={errores} avisoPersonId={aviso} />
        <div className="flex justify-end mt-6">
          <Button data-testid="cliente-agregar-btn" onClick={guardar} disabled={guardando}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
            {guardando ? "Guardando..." : "Agregar"}
          </Button>
        </div>
      </Panel>
    </div>
  );
}
